// Sprint 10 — Live View grid (rewrite).
//
// Top bar:
//   * Title
//   * Grid-size selector (1x1, 2x2, 3x3, 4x4, 5x5, 6x6, 8x8)
//   * Master audio toggle (mutes every tile in one click)
//   * Page-level fullscreen
//
// Per-tile:
//   * Default state = STOPPED. No hls.js instance, no network fetches.
//     The user explicitly clicks ▶ to start each stream. Power+bandwidth
//     friendly — opening Live View doesn't auto-load 64 streams in 8x8.
//   * Once playing, audio is muted by default (browser autoplay policy).
//     The user clicks the speaker icon to enable audio on ONE tile;
//     enabling audio on tile B mutes tile A. (One audio source is the
//     right default for security ops — you can hear one room at a time.)
//   * Per-tile fullscreen via the Fullscreen Element API.
//
// Grid assignment is persisted to localStorage keyed by grid size, so the
// arrangement survives reloads. (No drag-and-drop yet — slots auto-fill
// in camera-list order. dnd-kit is a Sprint-10.5 add.)
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CameraIcon,
  Gamepad2Icon,
  MaximizeIcon,
  PauseIcon,
  PlayIcon,
  ScissorsIcon,
  SquareIcon,
  Volume2Icon,
  VolumeXIcon,
  VideoOffIcon,
} from "lucide-react";

import { useNavigate, useSearchParams } from "react-router-dom";

import { useCameras } from "@/api/cameras";
import { useEntities, useEntityDevices, useEntityTree } from "@/api/entities";
import { nvrKey, nvrFilterOptions } from "@/lib/nvrKey";
import { useRecordingConfigs } from "@/api/recordings";
import { api } from "@/api/client";
import { ensureLiveView } from "@/api/liveView";
import { livenessMessage } from "@/lib/streamStaleness";
import { useStreamPlayback } from "@/lib/useStreamPlayback";
import { tileStateMeta } from "@/lib/tileState";
import { startClipMark, stopClipMark } from "@/api/clips";
import { useLayouts, useCreateLayout, useLayout } from "@/api/layouts";
import { PTZControls } from "@/components/PTZControls";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { QueryState } from "@/components/QueryState";
import { cn } from "@/lib/utils";
import { useFormatTimestamp } from "@/utils/datetime";


// ---------------------------------------------------------------------------
// Grid sizes
// ---------------------------------------------------------------------------
type GridSize = "1x1" | "2x2" | "3x3" | "4x4" | "5x5" | "6x6" | "8x8";

const GRID_SIZES: GridSize[] = ["1x1", "2x2", "3x3", "4x4", "5x5", "6x6", "8x8"];

const gridTiles: Record<GridSize, number> = {
  "1x1": 1, "2x2": 4, "3x3": 9, "4x4": 16,
  "5x5": 25, "6x6": 36, "8x8": 64,
};

const gridClass: Record<GridSize, string> = {
  "1x1": "grid grid-cols-1 gap-2",
  "2x2": "grid grid-cols-2 gap-2",
  "3x3": "grid grid-cols-3 gap-2",
  "4x4": "grid grid-cols-4 gap-2",
  "5x5": "grid grid-cols-5 gap-2",
  "6x6": "grid grid-cols-6 gap-2",
  "8x8": "grid grid-cols-8 gap-1.5",
};

function GridIcon({ size }: { size: GridSize }) {
  // Tiny SVG showing N×N cells. Reads at small sizes.
  const n = parseInt(size.split("x")[0], 10);
  const cell = 14 / n;
  const cells = [];
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      cells.push(
        <rect key={`${r}-${c}`}
          x={1 + c * (cell + 0.5)} y={1 + r * (cell + 0.5)}
          width={cell} height={cell} rx="0.5" />,
      );
    }
  }
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="currentColor">
      {cells}
    </svg>
  );
}


// ---------------------------------------------------------------------------
// Tile — owns the hls.js lifecycle when "playing".
// ---------------------------------------------------------------------------
interface TileCamera {
  id: string;
  name: string;
  status: string; // S18B-2 — per-tile online/offline badge
  hls_url: string | null;
  // S-XCODE — agent-relay cameras need ▶ warm-up (start relay + wait for the
  // H.265 source) before loading the H264 transcode URL.
  connection_mode?: string;
  audio_enabled: boolean;
  // S7 — PTZ joystick icon only renders when this is true. Independent
  // of ``is_ptz`` (hardware capability) — operators flip this per
  // camera to gate PTZ control.
  ptz_enabled: boolean;
  last_seen_at: string | null;
}

// Imperative handle a tile exposes to the parent so "Play All" /
// "Stop All" can drive each tile without lifting the entire stream
// state up to the page. Parent stores these in a ref-keyed map.
interface TileApi {
  play: () => void;
  stop: () => void;
  isStopped: () => boolean;
}

interface LiveTileProps {
  camera: TileCamera | null;
  unmutedId: string | null;
  onRequestAudio: (id: string, name: string) => void;
  onToast: (msg: string) => void;
  globalMuteVersion: number;
  registerApi?: (id: string, api: TileApi | null) => void;
  recording?: boolean;
  // S7 — feature wiring. Each is independent; absent callbacks just
  // hide the corresponding overlay button.
  onOpenPtz?: (cameraId: string) => void;
  activeMarkId?: string | null;
  onStartMark?: (cameraId: string) => void;
  onStopMark?: (markId: string, cameraName: string) => void;
}


// ---------------------------------------------------------------------------
// Snapshot — server-side JPEG via /api/v1/cameras/{id}/snapshot.
//
// We tried client-side ``canvas.toDataURL`` first and hit the cross-origin
// canvas-taint rule: HLS comes from the EC2 nip.io while the React app
// lives on railway.app, so the browser flags the canvas as "tainted" and
// every encode call throws SecurityError ("encode error" toast).
//
// Server-side fix: the FastAPI endpoint proxies through to an EC2 nginx
// hop that runs ``ffmpeg -vframes 1`` against mediamtx's loopback RTSP
// and streams the JPEG back. No canvas, no taint, works in Chrome +
// Safari + Firefox identically. The trade-off is a ~2-second round-trip
// (RTSP setup + IDR wait + WAN) vs the instant client-side path, but
// reliability beats latency here.
// ---------------------------------------------------------------------------
async function takeSnapshot(
  cameraId: string,
  cameraName: string,
  onToast: (msg: string) => void,
): Promise<void> {
  let blob: Blob;
  try {
    const r = await api.get(`/api/v1/cameras/${cameraId}/snapshot`, {
      responseType: "blob",
    });
    blob = r.data as Blob;
  } catch (e: unknown) {
    // axios surfaces non-2xx as a rejection. Distinguish "stream not
    // ready yet" (503 from upstream) from the generic upstream/network
    // failure so the operator gets an actionable message.
    const err = e as { response?: { status?: number } };
    const code = err.response?.status;
    if (code === 503) {
      onToast("Snapshot failed — stream not ready yet");
    } else if (code === 409) {
      onToast("Snapshot failed — camera has no stream key");
    } else {
      onToast("Snapshot failed — stream may not be ready");
    }
    return;
  }
  const stamp = new Date()
    .toISOString()
    .replace(/:/g, "-")
    .replace("T", "_")
    .split(".")[0];  // 2026-05-12_06-15-42
  const safeName = cameraName.replace(/[^a-z0-9_-]/gi, "_");
  const filename = `${safeName}_${stamp}.jpg`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Free the blob URL after the click — keep a short delay so Safari
  // (which queues the download asynchronously) actually fetches before
  // we revoke.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  onToast(`Snapshot saved — ${filename}`);
}

type TileState = "stopped" | "loading" | "playing" | "error";

function LiveTile({
  camera, unmutedId, onRequestAudio, onToast, globalMuteVersion, registerApi, recording,
  onOpenPtz, activeMarkId, onStartMark, onStopMark,
}: LiveTileProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // PLAYBACK CORE IS OWNED BY ``lib/useStreamPlayback`` — see its header. This
  // page previously ran a SECOND, detector-less hls.js player, which is why
  // /live showed 19-hour-old frames on 2026-08-01 while /grid did not.
  //
  // ``phase`` is tile-local orchestration ONLY (stopped / agent warm-up / warm-up
  // refused). Everything about the pipeline comes from the hook.
  const [phase, setPhase] = useState<"stopped" | "warming" | "warm-error" | "running">("stopped");
  // P1B-FE — coded tile state from the ensure response (CAPACITY/DEGRADED/
  // FAILOVER) so a capacity refusal reads "NVR at stream limit", never a blank.
  const [codedState, setCodedState] = useState<string | null>(null);
  // S7 — stream failover. Up to 5 retries with a 10s gap between
  // attempts; after that we surface "Camera offline" + last_seen_at.
  // The timer ref keeps the retry pending across re-renders without
  // re-triggering on every state change.
  const [retryCount, setRetryCount] = useState(0);
  const retryTimerRef = useRef<number | null>(null);
  const RETRY_MAX = 5;
  const RETRY_DELAY_MS = 10_000;
  // S-XCODE — monotonic play token. Bumped on every play()/teardown() so an
  // in-flight agent-camera warm-up loop can detect it was superseded (user
  // stopped the tile / started another) and abort before calling startPlayback.
  const playGenRef = useRef(0);
  // Stream-token re-mint is owned by ``useStreamPlayback`` (proactive timer +
  // reactive 401 path), so this page no longer keeps its own copy. Two
  // implementations of token re-mint is the same defect shape as two players.


  // S7 — digital zoom (CSS transform only — never re-requests the stream).
  // Per-tile to avoid one tile's wheel-scroll mutating another tile's
  // viewport.
  const [zoom, setZoom] = useState(1);
  const formatTs = useFormatTimestamp();

  // MEMOIZED, and this is load-bearing (2026-08-04). As an inline arrow this
  // had a new identity on every render; it sat in the playback effect's
  // dependency array, so every render tore the pipeline down and rebuilt it,
  // and hls.destroy() aborted whatever playlist or segment was in flight —
  // dozens of index.m3u8 requests cancelled at 0.0 kB, 15-53ms after issue.
  //
  // ``play`` is read through a ref rather than listed as a dependency: it is
  // redeclared every render (a plain function in the component body), so
  // depending on it would put the instability straight back. The hook also
  // reads its callbacks through refs, so this memo is belt-and-braces — the
  // structural fix is there, not here.
  const playRef = useRef<() => void>(() => {});
  const handleFatalError = useCallback(() => {
    setRetryCount((prev) => {
      const next = prev + 1;
      if (next <= RETRY_MAX) {
        if (retryTimerRef.current !== null) {
          window.clearTimeout(retryTimerRef.current);
        }
        retryTimerRef.current = window.setTimeout(() => {
          retryTimerRef.current = null;
          if (videoRef.current) playRef.current();
        }, RETRY_DELAY_MS);
      }
      return next;
    });
  }, [videoRef]);

  // Tear down hls.js on unmount or when stopped.
  // The one playback pipeline. autoStart=false because /live MUST run its
  // agent-relay warm-up first: every camera here is agent-relay H.265 whose
  // ``_h264`` transcode sibling spins up on demand, so loading the manifest
  // before the relay is ready fetches a path that does not exist yet.
  const playback = useStreamPlayback({
    videoRef,
    hlsUrl: camera?.hls_url,
    cameraId: camera?.id,
    enabled: phase === "running",
    autoplay: true,
    autoStart: false,
    onFatalError: handleFatalError,
  });

  // Presentation state. The hook owns the pipeline; the warm-up phases are the
  // only states this page still contributes.
  const state: TileState =
    phase === "warming" ? "loading"
      : phase === "warm-error" ? "error"
      : phase === "stopped" ? "stopped"
      : playback.state === "playing" ? "playing"
      : playback.state === "loading" ? "loading"
      : playback.state === "error" ? "error"
      : "stopped";

  function teardown() {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    // Invalidate any in-flight warm-up loop (S-XCODE).
    playGenRef.current += 1;
    setPhase("stopped");
    // The hook destroys hls AND blanks the element. Never just hide it: a
    // hidden <video> still holds its last decoded frame, so anything that
    // re-shows it repaints stale video.
    playback.stop();
  }

  // Master mute pulse — when the parent bumps globalMuteVersion, force
  // this video element to mute (regardless of unmutedId).
  useEffect(() => {
    if (videoRef.current && globalMuteVersion > 0) {
      videoRef.current.muted = true;
    }
  }, [globalMuteVersion]);

  // Whenever unmutedId changes, reflect on this video.
  //
  // When transitioning from muted -> unmuted we also (a) explicitly
  // enable every entry in the legacy ``audioTracks`` collection (Safari
  // sometimes leaves them disabled when the element was created muted)
  // and (b) re-trigger ``play()`` because Chrome/Firefox can pause a
  // muted-autoplayed element when its mute attribute flips, especially
  // when no user-gesture context is attached to the property write.
  useEffect(() => {
    if (!videoRef.current || !camera) return;
    const v = videoRef.current;
    const shouldMute = unmutedId !== camera.id;
    v.muted = shouldMute;
    if (!shouldMute) {
      const tracks = (v as unknown as { audioTracks?: { length: number; [i: number]: { enabled: boolean } } }).audioTracks;
      if (tracks) {
        for (let i = 0; i < tracks.length; i++) tracks[i].enabled = true;
      }
      void v.play().catch(() => { /* user gesture absent; click-to-play is fine */ });
    }
    // ``playback.state`` is a dep so the correct mute is re-applied the moment
    // playback begins. The hook mutes on autoplay (browsers require it), and
    // without this the per-tile unmute would be silently swallowed on every
    // play — audio would only work if unmutedId changed AFTER playback started.
  }, [unmutedId, camera, playback.state]);

  // Tear down on unmount.
  useEffect(() => () => teardown(), []);

  // Register imperative API with parent so "Play All" / "Stop All" can
  // drive this tile without lifting the entire hls.js lifecycle into
  // page-level state.
  useEffect(() => {
    if (!camera || !registerApi) return;
    registerApi(camera.id, {
      play: () => { if (state === "stopped") play(); },
      stop: () => { if (state !== "stopped") teardown(); },
      isStopped: () => state === "stopped",
    });
    return () => registerApi(camera.id, null);
    // ``state`` is intentionally a dep so the captured closure stays
    // up-to-date (the parent always sees fresh isStopped/play/stop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera?.id, state]);

  // S-XCODE — entry point. Agent-relay cameras stream H.265 on-demand, so before
  // the (H264 transcode) URL is loaded the branch relay must be pushing and the
  // H.265 source live. ``ensureLiveView`` starts the relay and reports source
  // readiness; we poll it, then hand the pipeline to the shared hook.
  //
  // THIS WARM-UP IS LOAD-BEARING and stays here deliberately: it is tile
  // orchestration, not playback, and every camera on this fleet is agent-relay
  // H.265 whose ``_h264`` sibling spins up on demand. Starting playback before
  // it is ready fetches a manifest that does not exist yet.
  function play() {
    if (!camera?.hls_url || !videoRef.current) return;
    const mode = camera.connection_mode;
    if (mode === "agent" || mode === "slink_agent") {
      void warmUpThenPlay();
    } else {
      setPhase("running");
      playback.start();
    }
  }

  // Keep the ref pointing at the CURRENT play closure (it captures camera,
  // phase and playback, all of which change). Assigned during render so the
  // retry timer always calls the live version, never a stale capture.
  playRef.current = play;

  async function warmUpThenPlay() {
    if (!camera) return;
    teardown();
    setPhase("warming");
    const gen = (playGenRef.current += 1);
    const cameraId = camera.id;
    const DEADLINE_MS = 25_000;
    const POLL_MS = 1_500;
    const startedAt = Date.now();
    while (Date.now() - startedAt < DEADLINE_MS) {
      try {
        const res = await ensureLiveView(cameraId);
        // Superseded (user stopped this tile / started another) — abort.
        if (playGenRef.current !== gen) return;
        // P1B-FE — carry the coded tile state (capacity/degraded/failover).
        setCodedState(res.tile_state ?? null);
        if (res.state === "ready") {
          setPhase("running");
          playback.start();
          return;
        }
        if (
          res.state === "unavailable" ||
          res.state === "busy" ||
          res.state === "degraded" ||
          // check_failed joins the honest-refusal set rather than the poll
          // loop: polling a broken readiness check just burns the 25s
          // deadline and lands on the same generic failure, which is exactly
          // the permanent spinner this state exists to end. The coded tile
          // state (CHECK_FAILED) is already set above, so the tile explains
          // itself instead of spinning.
          res.state === "check_failed"
        ) {
          // An honest refusal, immediately — not a 25s spinner into a generic
          // hls.js failure. Nothing is painted because nothing ever started.
          setPhase("warm-error");
          return;
        }
      } catch {
        // Warm-up endpoint unreachable — fall through to a normal play attempt;
        // the hook's retry ladder keeps trying if the source isn't up yet.
        break;
      }
      await new Promise((r) => window.setTimeout(r, POLL_MS));
      if (playGenRef.current !== gen) return;
    }
    // Deadline hit (or endpoint errored): hand over anyway — the hook's own
    // network-retry ladder and staleness clock take over from here, and the
    // staleness clock is what guarantees a never-started tile paints nothing.
    if (playGenRef.current === gen) {
      setPhase("running");
      playback.start();
    }
  }


  function toggleFullscreen() {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement === el) {
      void document.exitFullscreen();
    } else {
      void el.requestFullscreen?.();
    }
  }

  if (!camera) {
    return (
      <div className="aspect-video overflow-hidden rounded-md border border-dashed border-[#2d3f4f]/60 bg-[#21313c]/40">
        <div className="flex h-full w-full items-center justify-center text-[10px] text-white/30">
          empty slot
        </div>
      </div>
    );
  }

  const isMuted = unmutedId !== camera.id;
  const audioEnabled = camera.audio_enabled;
  const playing = state === "playing";

  // S7 — wheel handler for digital zoom. Step 0.5x per notch, capped
  // 1x–4x. Returns the rounded value so the badge reads "2.0x" not
  // "1.9999999x" after many notches.
  function handleWheel(e: React.WheelEvent<HTMLDivElement>) {
    if (state !== "playing") return;
    e.preventDefault();
    setZoom((prev) => {
      const delta = e.deltaY < 0 ? 0.5 : -0.5;
      const next = Math.min(4, Math.max(1, prev + delta));
      return Math.round(next * 10) / 10;
    });
  }

  function resetZoom() {
    setZoom(1);
  }

  return (
    <div
      ref={containerRef}
      className="group relative aspect-video overflow-hidden rounded-md border border-[#2d3f4f] bg-primary"
      onWheel={handleWheel}
      onDoubleClick={resetZoom}
    >
      {/* Top bar — always visible */}
      <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between gap-1 bg-gradient-to-b from-black/70 to-transparent px-2 py-1.5">
        <span className="truncate text-[11px] font-medium text-white/90">
          {/* S18B-2 — per-tile status badge (online / offline). */}
          <span
            className="mr-1 inline-block h-2 w-2 rounded-full align-middle"
            title={`status: ${camera.status}`}
            style={{
              backgroundColor:
                camera.status === "online"
                  ? "#10b981"
                  : camera.status === "offline" || camera.status === "error"
                    ? "#ef4444"
                    : "#9ca3af",
            }}
          />
          {camera.name}
        </span>
        {tileStateMeta(codedState) && (
          <span
            className="ml-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-white"
            data-testid="tile-coded-state"
            style={{ backgroundColor: tileStateMeta(codedState)!.color }}
          >
            {tileStateMeta(codedState)!.label}
          </span>
        )}
        <div className="flex items-center gap-1">
          {/* Audio toggle — always rendered. Disabled (grey) when the
              stream isn't playing or the camera has no audio track.
              Matches the snapshot button's always-visible / disabled
              pattern so the tile chrome is identical across cameras. */}
          <button
            type="button"
            onClick={() => {
              if (audioEnabled && playing) onRequestAudio(camera.id, camera.name);
            }}
            disabled={!playing || !audioEnabled}
            className={cn(
              "rounded p-1 transition",
              !playing || !audioEnabled
                ? "cursor-not-allowed text-white/30"
                : "text-white/70 hover:bg-white/10 hover:text-white",
            )}
            title={
              !playing
                ? "Start stream first"
                : !audioEnabled
                  ? "No audio on this camera"
                  : isMuted
                    ? "Unmute (mutes others)"
                    : "Muted"
            }
          >
            {isMuted
              ? <VolumeXIcon className="h-3 w-3" />
              : <Volume2Icon className="h-3 w-3 text-[#ff6d00]" />}
          </button>
          <button
            type="button"
            onClick={playing || state === "loading" ? teardown : play}
            className="rounded p-1 text-white/70 transition hover:bg-white/10 hover:text-white"
            title={playing ? "Stop" : "Play"}
          >
            {playing || state === "loading" ? (
              <PauseIcon className="h-3 w-3 text-[#ff6d00]" />
            ) : (
              <PlayIcon className="h-3 w-3" />
            )}
          </button>
          <button
            type="button"
            onClick={() => { void takeSnapshot(camera.id, camera.name, onToast); }}
            disabled={!playing}
            className={cn(
              "rounded p-1 transition",
              playing
                ? "text-white/70 hover:bg-white/10 hover:text-white"
                : "cursor-not-allowed text-white/30",
            )}
            title={playing ? "Take snapshot" : "Start stream to capture"}
          >
            <CameraIcon className="h-3 w-3" />
          </button>
          {/* S7 — PTZ joystick. Rendered only on PTZ-enabled cameras so
              non-PTZ tiles don't show a button that 400s on click. */}
          {camera.ptz_enabled && onOpenPtz && (
            <button
              type="button"
              onClick={() => onOpenPtz(camera.id)}
              className="rounded p-1 text-white/70 transition hover:bg-white/10 hover:text-white"
              title="PTZ controls"
            >
              <Gamepad2Icon className="h-3 w-3" />
            </button>
          )}
          {/* S7 — clip marking. Toggle: start when no mark active, stop
              when one is open. */}
          {(onStartMark || onStopMark) && (
            <button
              type="button"
              onClick={() => {
                if (activeMarkId && onStopMark) onStopMark(activeMarkId, camera.name);
                else if (onStartMark) onStartMark(camera.id);
              }}
              className={cn(
                "rounded p-1 transition",
                activeMarkId
                  ? "bg-red-500/20 text-red-300 hover:bg-red-500/30"
                  : "text-white/70 hover:bg-white/10 hover:text-white",
              )}
              title={activeMarkId ? "Stop clip mark" : "Mark clip"}
            >
              <ScissorsIcon className="h-3 w-3" />
            </button>
          )}
          {playing && (
            <button
              type="button"
              onClick={toggleFullscreen}
              className="rounded p-1 text-white/70 transition hover:bg-white/10 hover:text-white"
              title="Fullscreen"
            >
              <MaximizeIcon className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* Video / placeholder. The wrapper applies the CSS transform for
          digital zoom; the underlying video element is unaware so the
          HLS pipeline isn't disturbed. */}
      <video
        ref={videoRef}
        className={cn(
          "h-full w-full object-cover transition-transform duration-100",
          state === "playing" ? "block" : "hidden",
        )}
        style={{ transform: zoom > 1 ? `scale(${zoom})` : undefined, transformOrigin: "center" }}
        playsInline
        muted
      />

      {/* Zoom badge — only when actively zoomed in. */}
      {zoom > 1 && state === "playing" && (
        <div className="pointer-events-none absolute bottom-2 left-2 z-30 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
          {zoom.toFixed(1)}×
        </div>
      )}

      {/* S7 — clip-marking badge: pulsing red dot. Renders on top of
          the video so the operator sees the marking state from any
          part of the page. */}
      {activeMarkId && state === "playing" && (
        <div className="pointer-events-none absolute right-2 top-9 z-30 inline-flex items-center gap-1 rounded bg-red-600/80 px-1.5 py-0.5 text-[10px] font-medium text-white">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
          MARKING
        </div>
      )}

      {/* P0 — a stale / never-started stream takes precedence over every other
          state. The picture has ALREADY been cleared by the hook; this overlay is
          FULLY OPAQUE (bg-black) so no residual frame can bleed through and be
          mistaken for live video. This is what /live was missing on 2026-08-01. */}
      {playback.mustClear && phase === "running" ? (
        <div
          data-testid="stream-unavailable"
          data-liveness={playback.liveness.kind}
          className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-1 bg-black text-center text-red-200"
        >
          <VideoOffIcon className="h-6 w-6 text-destructive" />
          <span className="text-[10px]">{livenessMessage(playback.liveness)}</span>
          {/* Recovery is VISIBLY DISTINCT from a tile that never started: an
              operator watching a wall needs to know the difference between
              "this is coming back on its own" and "this is down and nothing
              further will happen". Both used to render as the same red
              square. */}
          {playback.recovery ? (
            <span
              data-testid="stream-recovery"
              data-exhausted={String(playback.recovery.exhausted)}
              className={
                playback.recovery.exhausted
                  ? "text-[10px] text-red-300/80"
                  : "text-[10px] text-amber-300/90"
              }
            >
              {playback.recovery.exhausted
                ? `Gave up after ${playback.recovery.max} attempts`
                : `Reconnecting ${playback.recovery.attempt}/${playback.recovery.max}…`}
            </span>
          ) : null}
          <span className="text-[10px] text-red-300/80">{camera.name}</span>
        </div>
      ) : null}

      {state === "stopped" && (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-[#21313c] text-white/40">
          <VideoOffIcon className="h-6 w-6" />
          <span className="px-2 text-center text-[10px]">Click ▶ to start</span>
        </div>
      )}
      {state === "loading" && (
        <div className="flex h-full w-full items-center justify-center bg-black/80">
          <div className="h-1.5 w-24 animate-pulse rounded-full bg-white/30" />
        </div>
      )}
      {state === "error" && (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-red-950/40 text-red-200">
          <VideoOffIcon className="h-6 w-6" />
          {retryCount < RETRY_MAX ? (
            <>
              <span className="text-[10px]">Stream unavailable</span>
              <span className="text-[10px] text-red-300">
                Retrying… ({retryCount}/{RETRY_MAX})
              </span>
              <button
                type="button"
                onClick={() => { setRetryCount(0); play(); }}
                className="mt-1 rounded bg-[#ff6d00] px-2 py-0.5 text-[10px] font-medium text-white hover:bg-[#ff6d00]/90"
              >
                Retry now
              </button>
            </>
          ) : (
            <>
              <span className="text-[10px] font-medium">Camera offline</span>
              {camera.last_seen_at && (
                <span className="text-[10px] text-red-300/80">
                  Last seen {formatTs(camera.last_seen_at, "relative")}
                </span>
              )}
              <button
                type="button"
                onClick={() => { setRetryCount(0); play(); }}
                className="mt-1 rounded bg-[#ff6d00] px-2 py-0.5 text-[10px] font-medium text-white hover:bg-[#ff6d00]/90"
              >
                Retry
              </button>
            </>
          )}
        </div>
      )}

      {/* Bottom bar (hover) */}
      {playing && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 to-transparent px-2 py-1 text-[10px] text-white/70 opacity-0 transition-opacity group-hover:opacity-100">
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            LIVE
          </span>
          {recording && (
            <span className="inline-flex items-center gap-1 text-red-300">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
              REC
            </span>
          )}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
// S12 — collects camera IDs assigned to an entity or any of its
// descendants. Returns a stable empty set when no entity is selected.
function useEntityCameraIds(
  rootId: string | null,
  subtreeIds: string[],
): Set<string> {
  const allIds = rootId ? [rootId, ...subtreeIds.filter((id) => id !== rootId)] : [];
  // Hooks must be unconditional. We always render the same number of
  // useEntityDevices calls, by capping the subtree at MAX_NODES and
  // padding with undefined ids.
  const MAX_NODES = 32;
  const padded = allIds.slice(0, MAX_NODES);
  while (padded.length < MAX_NODES) padded.push("");
  // A28 — one unconditional hook per slot for the FULL MAX_NODES (32). Previously only
  // 16 hooks existed, so entity subtrees with a 17th+ node silently dropped cameras.
  const d0 = useEntityDevices(padded[0] || undefined);
  const d1 = useEntityDevices(padded[1] || undefined);
  const d2 = useEntityDevices(padded[2] || undefined);
  const d3 = useEntityDevices(padded[3] || undefined);
  const d4 = useEntityDevices(padded[4] || undefined);
  const d5 = useEntityDevices(padded[5] || undefined);
  const d6 = useEntityDevices(padded[6] || undefined);
  const d7 = useEntityDevices(padded[7] || undefined);
  const d8 = useEntityDevices(padded[8] || undefined);
  const d9 = useEntityDevices(padded[9] || undefined);
  const d10 = useEntityDevices(padded[10] || undefined);
  const d11 = useEntityDevices(padded[11] || undefined);
  const d12 = useEntityDevices(padded[12] || undefined);
  const d13 = useEntityDevices(padded[13] || undefined);
  const d14 = useEntityDevices(padded[14] || undefined);
  const d15 = useEntityDevices(padded[15] || undefined);
  const d16 = useEntityDevices(padded[16] || undefined);
  const d17 = useEntityDevices(padded[17] || undefined);
  const d18 = useEntityDevices(padded[18] || undefined);
  const d19 = useEntityDevices(padded[19] || undefined);
  const d20 = useEntityDevices(padded[20] || undefined);
  const d21 = useEntityDevices(padded[21] || undefined);
  const d22 = useEntityDevices(padded[22] || undefined);
  const d23 = useEntityDevices(padded[23] || undefined);
  const d24 = useEntityDevices(padded[24] || undefined);
  const d25 = useEntityDevices(padded[25] || undefined);
  const d26 = useEntityDevices(padded[26] || undefined);
  const d27 = useEntityDevices(padded[27] || undefined);
  const d28 = useEntityDevices(padded[28] || undefined);
  const d29 = useEntityDevices(padded[29] || undefined);
  const d30 = useEntityDevices(padded[30] || undefined);
  const d31 = useEntityDevices(padded[31] || undefined);
  const all = [
    d0, d1, d2, d3, d4, d5, d6, d7, d8, d9, d10, d11, d12, d13, d14, d15,
    d16, d17, d18, d19, d20, d21, d22, d23, d24, d25, d26, d27, d28, d29, d30, d31,
  ];
  return useMemo(() => {
    const out = new Set<string>();
    for (const q of all) {
      for (const dev of q.data ?? []) {
        if (dev.device_type === "camera") out.add(dev.device_id);
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, all.map((q) => q.data));
}

function readGridSize(): GridSize {
  try {
    const v = localStorage.getItem("sview360.liveview.gridSize");
    if (v && (GRID_SIZES as string[]).includes(v)) return v as GridSize;
  } catch {/* localStorage unavailable */}
  return "2x2";
}

export function LiveView() {
  const [gridSize, setGridSize] = useState<GridSize>(readGridSize);
  const [unmutedId, setUnmutedId] = useState<string | null>(null);
  const [globalMuteVersion, setGlobalMuteVersion] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  // S7 — page-level state for the new features.
  const [activePTZCameraId, setActivePTZCameraId] = useState<string | null>(null);
  const [activeMarks, setActiveMarks] = useState<Record<string, string>>({});
  const [isVideoWall, setIsVideoWall] = useState(false);
  const [showVWBar, setShowVWBar] = useState(true);
  const [layoutSaveOpen, setLayoutSaveOpen] = useState(false);
  const [layoutSaveName, setLayoutSaveName] = useState("");
  const nav = useNavigate();
  // Imperative tile registry — each LiveTile registers {play, stop,
  // isStopped} keyed by camera id on mount and clears it on unmount.
  // "Play All" / "Stop All" walk this map instead of lifting tile
  // state up to the page.
  const tileApisRef = useRef<Record<string, TileApi>>({});
  const registerTileApi = useCallback((id: string, api: TileApi | null) => {
    if (api) tileApisRef.current[id] = api;
    else delete tileApisRef.current[id];
  }, []);

  const totalSlots = gridTiles[gridSize];
  // FE BUG 3 — fetch a wider set so the user can PICK which cameras to view
  // (the grid still renders only totalSlots). Per-camera selection + the entity
  // filter together drive which cameras land in the grid.
  const { data, isLoading, isError, error, refetch } = useCameras({ limit: 100 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // S12 — entity filter. URL ?entity=<id> persists across reloads and
  // is shareable. We pull the subtree's camera bindings via two cheap
  // queries (subtree + devices).
  const [params, setParams] = useSearchParams();
  const entityFilter = params.get("entity");
  const layoutFilter = params.get("layout");
  // S7 — layouts. The list drives the dropdown; the detail query is
  // only enabled when ?layout= is in the URL (avoids fetching every
  // layout's cells on every navigation).
  const layoutsQuery = useLayouts();
  const layoutDetail = useLayout(layoutFilter);
  const createLayout = useCreateLayout();
  const { data: entityRoots = [] } = useEntities();
  const { data: entitySubtree } = useEntityTree(entityFilter ?? undefined);
  const subtreeIds = useMemo(
    () => new Set((entitySubtree ?? []).map((e) => e.id)),
    [entitySubtree],
  );
  // Fetch devices for the selected entity. (Subtree-wide attachment
  // happens via entity_devices on descendants; we keep this simple by
  // pulling each node's devices in parallel. For a 5-node subtree the
  // cost is negligible.)
  const allowedCameraIds = useEntityCameraIds(
    entityFilter,
    Array.from(subtreeIds),
  );
  // Batch 1 (UX/IA) — NVR filter dimension alongside the entity filter.
  // Persists via ?nvr=<key> (same pattern as ?entity=). Uses the shared
  // nvrKey so the scoping matches the Cameras tab exactly.
  const nvrFilter = params.get("nvr");
  const nvrOptions = useMemo(
    () => nvrFilterOptions(data?.items ?? []),
    [data],
  );
  const matchesNvr = useCallback(
    (c: { source_id?: string | null; nvr_serial?: string | null; nvr_make?: string | null; nvr_model?: string | null }) =>
      !nvrFilter || nvrKey(c) === nvrFilter,
    [nvrFilter],
  );
  // FE BUG 3 — the cameras the selector offers: the full list, narrowed by the
  // active entity + NVR filters. The checkbox picks drive the grid (below).
  const availableCameras = useMemo(
    () =>
      (data?.items ?? []).filter(
        (c) => (entityFilter ? allowedCameraIds.has(c.id) : true) && matchesNvr(c),
      ),
    [data, entityFilter, allowedCameraIds, matchesNvr],
  );
  const recConfigs = useRecordingConfigs();
  const recordingByCamera = (recConfigs.data?.items ?? []).reduce<
    Record<string, boolean>
  >((acc, c) => {
    acc[c.camera_id] = c.enabled;
    return acc;
  }, {});

  useEffect(() => {
    try { localStorage.setItem("sview360.liveview.gridSize", gridSize); } catch {/* ignore */}
  }, [gridSize]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  // Map each grid slot to a camera, padding with nulls when cameras < slots.
  // S7 — when a layout is loaded via ?layout=, its cells dictate which
  // camera lives in which slot. Otherwise we fall back to the old
  // sequential fill from the camera list.
  const tiles: (TileCamera | null)[] = useMemo(() => {
    const filtered = (data?.items ?? []).filter(
      (c) => (entityFilter ? allowedCameraIds.has(c.id) : true) && matchesNvr(c),
    );
    const byId: Record<string, TileCamera> = {};
    for (const c of filtered) {
      byId[c.id] = {
        id: c.id,
        name: c.name,
        status: (c as { status?: string }).status ?? "pending",
        hls_url: c.hls_url ?? null,
        connection_mode: (c as { connection_mode?: string }).connection_mode,
        audio_enabled: (c as { audio_enabled?: boolean }).audio_enabled ?? c.has_audio,
        ptz_enabled: Boolean(
          (c as { ptz_enabled?: boolean }).ptz_enabled,
        ),
        last_seen_at:
          (c as { last_seen_at?: string | null }).last_seen_at ?? null,
      };
    }
    const items: (TileCamera | null)[] = [];
    const layoutCells = layoutDetail.data?.layout_config?.cells;
    if (layoutFilter && layoutCells && layoutCells.length > 0) {
      // Layout-driven assignment. Cells may reference cameras the
      // user can't see (deleted / out-of-scope) — null them out so
      // the slot renders as "empty slot" rather than blowing up.
      for (let i = 0; i < totalSlots; i++) {
        const cell = layoutCells.find((c) => c.position === i);
        const camId = cell?.camera_id ?? null;
        items.push(camId && byId[camId] ? byId[camId] : null);
      }
    } else {
      // FE BUG 3 — when the user has hand-picked cameras, render exactly those;
      // otherwise the default sequential fill of the (entity-filtered) list.
      const pick =
        selectedIds.size > 0
          ? filtered.filter((c) => selectedIds.has(c.id))
          : filtered;
      for (const c of pick.slice(0, totalSlots)) items.push(byId[c.id]);
      while (items.length < totalSlots) items.push(null);
    }
    return items;
  }, [data, totalSlots, entityFilter, allowedCameraIds, matchesNvr, layoutFilter, layoutDetail.data, selectedIds]);

  // S7 — when ?layout= specifies a grid size that differs from the
  // current selection, follow the layout. The user can still override
  // by clicking a grid-size button after the layout is loaded.
  useEffect(() => {
    const gs = layoutDetail.data?.grid_size;
    if (gs && gs !== gridSize) {
      setGridSize(gs as GridSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutDetail.data?.grid_size]);

  function handleRequestAudio(id: string, name: string) {
    if (unmutedId === id) {
      setUnmutedId(null);
      setToast(`Muted ${name}`);
    } else {
      setUnmutedId(id);
      setToast(`Audio: ${name}`);
    }
  }

  function masterMute() {
    setUnmutedId(null);
    setGlobalMuteVersion((v) => v + 1);
    setToast("All audio muted");
  }

  // Play All — start every currently-stopped tile, staggered 300ms apart
  // so 5+ simultaneous HLS playlist fetches don't hammer mediamtx's
  // on-demand path warm-up. Tiles already playing are skipped.
  function playAll() {
    const stoppedIds = Object.entries(tileApisRef.current)
      .filter(([, api]) => api.isStopped())
      .map(([id]) => id);
    if (stoppedIds.length === 0) {
      setToast("All streams already playing");
      return;
    }
    stoppedIds.forEach((id, idx) => {
      setTimeout(() => tileApisRef.current[id]?.play(), idx * 300);
    });
    setToast(`Starting ${stoppedIds.length} stream${stoppedIds.length === 1 ? "" : "s"}`);
  }

  function stopAll() {
    const playingIds = Object.entries(tileApisRef.current)
      .filter(([, api]) => !api.isStopped())
      .map(([id]) => id);
    if (playingIds.length === 0) {
      setToast("No streams playing");
      return;
    }
    playingIds.forEach((id) => tileApisRef.current[id]?.stop());
    setUnmutedId(null);
    setToast(`Stopped ${playingIds.length} stream${playingIds.length === 1 ? "" : "s"}`);
  }

  function pageFullscreen() {
    const root = document.getElementById("liveview-grid-root");
    if (!root) return;
    if (document.fullscreenElement === root) {
      void document.exitFullscreen();
    } else {
      void root.requestFullscreen?.();
    }
  }

  // S7 — Video Wall: an opinionated fullscreen on the document root
  // with auto-hiding chrome. Distinct from pageFullscreen (which is
  // just an Fullscreen API call on the grid). The auto-hide effect
  // listens to mousemove on the document for the duration of the
  // session.
  function enterVideoWall() {
    void document.documentElement.requestFullscreen?.();
    setIsVideoWall(true);
    setShowVWBar(true);
  }

  function exitVideoWall() {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    }
    setIsVideoWall(false);
  }

  useEffect(() => {
    if (!isVideoWall) return;
    let timer: number | null = null;
    const onMove = () => {
      setShowVWBar(true);
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => setShowVWBar(false), 3000);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") exitVideoWall();
    };
    const onFsChange = () => {
      if (!document.fullscreenElement) {
        // User exited via OS shortcut; sync our state.
        setIsVideoWall(false);
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFsChange);
    onMove();
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFsChange);
      if (timer !== null) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideoWall]);

  // S7 — PTZ panel: only one open at a time. Clicking the same camera's
  // joystick twice closes it.
  function handleOpenPtz(cameraId: string) {
    setActivePTZCameraId((prev) => (prev === cameraId ? null : cameraId));
  }

  // S7 — clip marking.
  async function handleStartMark(cameraId: string) {
    try {
      const mark = await startClipMark(cameraId);
      setActiveMarks((prev) => ({ ...prev, [cameraId]: mark.id }));
      setToast("Clip mark started");
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { detail?: string } } };
      setToast(
        err.response?.status === 403
          ? "Clip marking requires export permission"
          : err.response?.data?.detail ?? "Failed to start clip mark",
      );
    }
  }

  async function handleStopMark(markId: string, cameraName: string) {
    try {
      await stopClipMark(markId);
      setActiveMarks((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          if (next[k] === markId) delete next[k];
        }
        return next;
      });
      setToast(`Clip marked — ${cameraName}. View in Evidence.`);
    } catch {
      setToast("Failed to stop clip mark");
    }
  }

  // S7 — save current layout. The backend's grid_size whitelist is a
  // subset of LiveView's (no 5x5 / 6x6 / 8x8), so block the save when
  // the user is on one of those — saving with an unsupported grid
  // would 400 anyway and confuse the user.
  const LAYOUT_API_GRID_SIZES = new Set<string>([
    "1x1", "1x2", "2x2", "2x3", "3x3", "3x4", "4x4",
  ]);
  async function handleSaveLayout() {
    if (!layoutSaveName.trim()) {
      setToast("Enter a layout name");
      return;
    }
    if (!LAYOUT_API_GRID_SIZES.has(gridSize)) {
      setToast(`Layouts don't support ${gridSize} grids — switch to a smaller grid first`);
      return;
    }
    try {
      const cells = tiles.map((cam, idx) => ({
        position: idx,
        camera_id: cam?.id ?? null,
        label: "",
      }));
      await createLayout.mutateAsync({
        name: layoutSaveName.trim(),
        // Cast: validated by the whitelist guard above.
        grid_size: gridSize as "1x1" | "1x2" | "2x2" | "2x3" | "3x3" | "3x4" | "4x4",
        layout_config: { cells },
      });
      setLayoutSaveOpen(false);
      setLayoutSaveName("");
      setToast(`Layout "${layoutSaveName.trim()}" saved`);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setToast(err.response?.data?.detail ?? "Failed to save layout");
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Live View</h1>
          {/* T2e — this was `data ? … : "Loading…"`, so a failed fetch showed
              "Loading…" forever: the spinner-that-never-resolves. */}
          <p className="text-sm text-muted-foreground">
            {isError
              ? `Camera list unavailable · ${gridSize}`
              : data
                ? `${Math.min(tiles.filter(Boolean).length, totalSlots)} of ${data.total} cameras · ${gridSize}`
                : "Loading…"}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* S12 — entity filter dropdown (persists via ?entity=) */}
          <select
            value={entityFilter ?? ""}
            onChange={(e) => {
              setSelectedIds(new Set()); // FE BUG 3 — reset picks when scope changes
              const next = new URLSearchParams(params);
              if (e.target.value) next.set("entity", e.target.value);
              else next.delete("entity");
              setParams(next, { replace: true });
            }}
            className="rounded-md border border-border bg-background px-2 py-1 text-xs"
          >
            <option value="">All entities</option>
            {entityRoots.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
            {(entitySubtree ?? [])
              .filter((e) => e.id !== entityFilter)
              .map((e) => (
                <option key={e.id} value={e.id}>
                  &nbsp;&nbsp;{e.name}
                </option>
              ))}
          </select>

          {/* Batch 1 (UX/IA) — NVR filter dropdown (persists via ?nvr=). Lets
              an operator scope the wall to a single recorder. */}
          <select
            value={nvrFilter ?? ""}
            onChange={(e) => {
              setSelectedIds(new Set()); // reset picks when scope changes
              const next = new URLSearchParams(params);
              if (e.target.value) next.set("nvr", e.target.value);
              else next.delete("nvr");
              setParams(next, { replace: true });
            }}
            className="rounded-md border border-border bg-background px-2 py-1 text-xs"
          >
            <option value="">All NVRs</option>
            {nvrOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          {/* FE BUG 3 — per-camera picker. The entity dropdown narrows the list;
              checking cameras here drives exactly which land in the grid. With
              nothing checked the grid keeps its default sequential fill. */}
          <details className="relative">
            <summary className="cursor-pointer list-none rounded-md border border-border bg-background px-2 py-1 text-xs">
              Cameras {selectedIds.size > 0 ? `(${selectedIds.size})` : "(all)"}
            </summary>
            <div className="absolute z-30 mt-1 max-h-64 w-60 overflow-y-auto rounded-md border border-border bg-background p-2 shadow-lg">
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="mb-1 w-full rounded px-1 py-0.5 text-left text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                Show all ({availableCameras.length})
              </button>
              {/* T2e — "No cameras in scope." used to render for a FAILED
                  fetch as well as an empty one, because `availableCameras`
                  derives from `data?.items ?? []`. "0 / 15" on a wall that
                  should show 15 read as a scoping decision the operator had
                  made, not as a request that never landed. The filters ARE
                  innocent here: `matchesNvr` short-circuits true when no NVR
                  filter is set, so an unfiltered wall can only be empty if the
                  fetch returned nothing. */}
              {isError ? (
                <p className="px-1 py-1 text-[11px] text-destructive">
                  Camera list failed to load — this is not an empty scope.
                </p>
              ) : availableCameras.length === 0 ? (
                <p className="px-1 py-1 text-[11px] text-muted-foreground">
                  {entityFilter || nvrFilter
                    ? "No cameras match the active filter."
                    : "No cameras in scope."}
                </p>
              ) : (
                availableCameras.map((c) => (
                  <label
                    key={c.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-muted"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      onChange={() => toggleSelected(c.id)}
                    />
                    <span className="flex-1 truncate">{c.name}</span>
                  </label>
                ))
              )}
            </div>
          </details>

          {/* S7 — Layout selector. ``__save__`` and ``__manage__`` are
              special sentinel option values that trigger actions rather
              than selecting a real layout. */}
          <select
            value={layoutFilter ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__save__") {
                setLayoutSaveOpen(true);
                return;
              }
              if (v === "__manage__") {
                nav("/layouts");
                return;
              }
              const next = new URLSearchParams(params);
              if (v) next.set("layout", v);
              else next.delete("layout");
              setParams(next, { replace: true });
            }}
            className="rounded-md border border-border bg-background px-2 py-1 text-xs"
          >
            <option value="">No layout</option>
            {(layoutsQuery.data ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name} ({l.grid_size})
              </option>
            ))}
            <option value="__save__">＋ Save current as layout…</option>
            <option value="__manage__">⚙ Manage layouts…</option>
          </select>

          {/* Grid selector */}
          <div className="flex items-center rounded-md border border-border bg-background p-0.5">
            {GRID_SIZES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setGridSize(s)}
                title={s}
                className={cn(
                  "inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] transition",
                  gridSize === s
                    ? "bg-primary text-white"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                <GridIcon size={s} />
                <span>{s}</span>
              </button>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={playAll}
            title="Start every stopped tile"
          >
            <PlayIcon className="mr-1 h-3.5 w-3.5" /> Play All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={stopAll}
            title="Stop every playing tile"
          >
            <SquareIcon className="mr-1 h-3.5 w-3.5" /> Stop All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={masterMute}
            title="Mute every tile"
          >
            <VolumeXIcon className="mr-1 h-3.5 w-3.5" /> All Mute
          </Button>
          <Button variant="outline" size="sm" onClick={pageFullscreen}>
            <MaximizeIcon className="mr-1 h-3.5 w-3.5" /> Fullscreen
          </Button>
          {/* S7 — Video Wall: opinionated fullscreen with auto-hiding
              chrome. Distinct from page fullscreen above. */}
          <Button variant="outline" size="sm" onClick={enterVideoWall}>
            <MaximizeIcon className="mr-1 h-3.5 w-3.5" /> Video Wall
          </Button>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-md bg-primary px-3 py-1.5 text-xs text-white shadow-lg">
          {toast}
        </div>
      )}

      {/* T2e — a failed camera fetch fell through to the grid and rendered
          `totalSlots` black tiles, which is indistinguishable from a wall of
          cameras that are all offline. Name the failure instead. */}
      {isError ? (
        <QueryState
          isLoading={false}
          isError
          error={error}
          label="the camera list"
          onRetry={refetch}
        >
          {null}
        </QueryState>
      ) : isLoading ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Loading cameras…
          </CardContent>
        </Card>
      ) : (
        <div
          id="liveview-grid-root"
          className={cn(gridClass[gridSize], "rounded-md bg-[#0f1923] p-2")}
        >
          {tiles.map((cam, idx) => (
            <LiveTile
              key={cam ? cam.id : `empty-${idx}`}
              camera={cam}
              unmutedId={unmutedId}
              onRequestAudio={handleRequestAudio}
              onToast={setToast}
              globalMuteVersion={globalMuteVersion}
              registerApi={registerTileApi}
              recording={cam ? recordingByCamera[cam.id] ?? false : false}
              onOpenPtz={handleOpenPtz}
              activeMarkId={cam ? activeMarks[cam.id] ?? null : null}
              onStartMark={handleStartMark}
              onStopMark={handleStopMark}
            />
          ))}
        </div>
      )}

      {/* S7 — PTZ floating panel. One at a time. */}
      {activePTZCameraId && (() => {
        const cam = (data?.items ?? []).find((c) => c.id === activePTZCameraId);
        return (
          <PTZControls
            cameraId={activePTZCameraId}
            cameraName={cam?.name ?? "Camera"}
            onClose={() => setActivePTZCameraId(null)}
          />
        );
      })()}

      {/* S7 — Save current layout modal */}
      {layoutSaveOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-80 rounded-md border border-border bg-background p-4">
            <h2 className="mb-2 text-sm font-semibold">Save current layout</h2>
            <p className="mb-3 text-xs text-muted-foreground">
              Grid: {gridSize}. {tiles.filter(Boolean).length} cameras placed.
            </p>
            <input
              autoFocus
              value={layoutSaveName}
              onChange={(e) => setLayoutSaveName(e.target.value)}
              placeholder="Layout name"
              className="mb-3 w-full rounded border border-border bg-background px-2 py-1 text-sm"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setLayoutSaveOpen(false);
                  setLayoutSaveName("");
                }}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={() => void handleSaveLayout()}>
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* S7 — Video Wall mini-bar (bottom center, auto-hiding). The
          fullscreen API itself handles the visual fullscreen; this
          element renders inside the fullscreen document so it's
          visible during video wall mode. */}
      {isVideoWall && showVWBar && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-md bg-primary/90 px-3 py-2 text-xs text-white shadow-xl">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exitVideoWall}>
              Exit
            </Button>
            <select
              value={layoutFilter ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                const next = new URLSearchParams(params);
                if (v) next.set("layout", v);
                else next.delete("layout");
                setParams(next, { replace: true });
              }}
              className="rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground"
            >
              <option value="">No layout</option>
              {(layoutsQuery.data ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
            <Button variant="outline" size="sm" onClick={playAll}>
              <PlayIcon className="mr-1 h-3 w-3" /> Play All
            </Button>
            <Button variant="outline" size="sm" onClick={stopAll}>
              <SquareIcon className="mr-1 h-3 w-3" /> Stop All
            </Button>
            <span className="text-[10px] text-white/50">Esc to exit</span>
          </div>
        </div>
      )}
    </div>
  );
}
